// @flow

import { default as React, useCallback, type ComponentType } from "react";
import type Moment from "moment";
import moment from "moment"; // eslint-disable-line no-duplicate-imports
import styled, { css } from "styled-components";
import Colors from "app/constants/analytics/Colors";
import Tooltip from "app/components/analytics/shared/Tooltip";

export type DateFilter = {| label: string, from: Moment | string |};

type Props = {
  setFilter: (DateFilter) => void,
  filter: DateFilter,
  heading?: string,
  subHeading?: string,
  className?: string,
  tooltip?: {
    name: string,
    description: string,
  },
};

const StyledFilters = styled.div`
  display: flex;
  justify-content: space-between;

  @media (min-width: 487px) {
    align-items: center;
  }

  @media (max-width: 486px) {
    flex-direction: column;
    align-items: left;
  }
`;

export const LAST_7_DAYS = {
  label: "Last 7 days",
  from: moment().subtract(6, "days").utc(),
};
export const LAST_14_DAYS = {
  label: "Last 14 days",
  from: moment().subtract(13, "days").utc(),
};
export const LAST_28_DAYS = {
  label: "Last 28 days",
  from: moment().subtract(27, "days").utc(),
};

export const DATE_FILTERS = [LAST_7_DAYS, LAST_14_DAYS, LAST_28_DAYS];

const DateFilters: ComponentType<Props> = (props: Props) => (
  <StyledFilters className={props.className}>
    {props.heading != null && (
      <h2 className="mt-4" color={Colors.DARK_GRAY}>
        {props.heading}
      </h2>
    )}
    {props.subHeading != null && (
      <div className="flex items-center">
        <h3 color={Colors.DARK_GRAY}>{props.subHeading}</h3>
        {props.tooltip && (
          <Tooltip classNames="ml-2" name={props.tooltip.name}>
            <>
              <p className="font-semibold m-0">{props.subHeading}</p>
              <p
                className="m-0 mt-1"
                dangerouslySetInnerHTML={{ __html: props.tooltip.description }}
              />
            </>
          </Tooltip>
        )}
      </div>
    )}
    <div>
      {DATE_FILTERS.map((filter) => {
        const isActive = props.filter.label === filter.label;
        const handleClick = useCallback(() => props.setFilter(filter), []);

        return (
          <FilterButton
            className="m1"
            key={filter.label}
            isActive={isActive}
            onClick={handleClick}
          >
            {filter.label}
          </FilterButton>
        );
      })}
    </div>
  </StyledFilters>
);

const FilterButton = styled.button`
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  display: inline-block;
  line-height: 1;
  padding: 0.5em 0.75em;
  height: auto;
  vertical-align: middle;
  -webkit-appearance: none;
  background-color: transparent;
  color: ${(props) => (props.isActive ? Colors.DARK_GRAY : Colors.MID_GRAY)};
  border: ${(props) =>
    props.isActive ? `1px solid ${Colors.MID_GRAY}` : "1px solid transparent"};
  border-radius: 4px;
  white-space: nowrap;
  font-weight: ${(props) => (props.isActive ? "600" : "400")};
  ${(props) =>
    !props.isActive &&
    css`
      &:focus,
      &:hover {
        color: ${Colors.DARK_GRAY};
        border: 1px solid ${Colors.MID_GRAY};
        font-weight: 400;
      }
    `}
`;
FilterButton.displayName = "FilterButton";

export default DateFilters;
